import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { aiError } from "./aiError";

const newId = () => `c_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const sortByTime = (arr) => [...arr].sort((a, b) => a.t - b.t);

// Timed comments for one project: persisted on the project, AI-polished on add.
export function useComments(project) {
  const [comments, setComments] = useState([]);
  const [improvingIds, setImprovingIds] = useState(new Set());
  const saveTimer = useRef(null);
  const latest = useRef([]);

  useEffect(() => { if (project) { const c = sortByTime(project.comments || []); latest.current = c; setComments(c); } }, [project?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = (next) => {
    latest.current = next;
    setComments(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.patch(`/projects/${project.project_id}`, { comments: latest.current }).catch(() => {});
    }, 600);
  };
  const setBusy = (id, on) => setImprovingIds((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });

  const add = (t, text) => {
    persist(sortByTime([...latest.current, { id: newId(), t: Math.max(0, Math.round(t * 10) / 10), text }]));
  };
  const polish = async (id, text) => {
    setBusy(id, true);
    try {
      const r = await api.post("/ai/improve", { text, source_title: project.source_title || "", fresh: true });
      persist(latest.current.map((c) => (c.id === id ? { ...c, text: r.data.text } : c)));
    } catch (e) {
      await aiError(e, "Couldn't re-polish this comment. Your text was kept.");
    } finally {
      setBusy(id, false);
    }
  };
  const repolish = (id) => {
    const c = latest.current.find((x) => x.id === id);
    if (c?.text.trim()) polish(id, c.text.trim());
  };
  const change = (id, text) => persist(latest.current.map((c) => (c.id === id ? { ...c, text } : c)));
  const remove = (id) => persist(latest.current.filter((c) => c.id !== id));

  return { comments, improvingIds, add, repolish, change, remove };
}
